import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

const FALLBACK_ICE = {
  iceServers: [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }],
};
const CHUNK_MS = 4000;

let icePromise = null;
const loadIce = () => {
  if (!icePromise) {
    icePromise = api
      .get("/rtc/ice")
      .then(({ data }) => ({ iceServers: data.iceServers || FALLBACK_ICE.iceServers }))
      .catch(() => FALLBACK_ICE);
  }
  return icePromise;
};

// WebRTC mesh call (voice or video) with Whisper-powered translated captions.
export function useCall({ roomId, send, myLang }) {
  const [inCall, setInCall] = useState(false);
  const [video, setVideo] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [muted, setMuted] = useState(false);
  const [participants, setParticipants] = useState({}); // user_id -> { name, stream }
  const [captions, setCaptions] = useState([]);
  const [captionsOn, setCaptionsOn] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const [speaking, setSpeaking] = useState({});
  const [quality, setQuality] = useState("good"); // good | ok | poor
  const [reconnecting, setReconnecting] = useState(false);

  const localStreamRef = useRef(null);
  const peersRef = useRef({});
  const captionTimerRef = useRef(null);
  const inCallRef = useRef(false);
  const callIdRef = useRef(null);
  const iceRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analysersRef = useRef({});

  useEffect(() => {
    loadIce().then((cfg) => (iceRef.current = cfg));
  }, []);

  const attachStream = (id, name, stream) =>
    setParticipants((p) => ({ ...p, [id]: { name, stream } }));

  const makePeer = useCallback(
    (peerId, peerName, initiator) => {
      if (peersRef.current[peerId]) return peersRef.current[peerId];
      const pc = new RTCPeerConnection(iceRef.current || FALLBACK_ICE);
      peersRef.current[peerId] = pc;
      localStreamRef.current?.getTracks().forEach((t) => pc.addTrack(t, localStreamRef.current));
      pc.onicecandidate = (e) => {
        if (e.candidate) send({ type: "signal", to_id: peerId, signal: { candidate: e.candidate } });
      };
      pc.ontrack = (e) => attachStream(peerId, peerName, e.streams[0]);
      pc.oniceconnectionstatechange = () => {
        if (pc.iceConnectionState === "disconnected" || pc.iceConnectionState === "failed") {
          setReconnecting(true);
          try {
            pc.restartIce?.();
          } catch (err) {
            console.debug("call: could not restart ICE", err);
          }
        } else if (pc.iceConnectionState === "connected" || pc.iceConnectionState === "completed") {
          setReconnecting(false);
        }
      };
      pc.onconnectionstatechange = () => {
        if (["failed", "closed"].includes(pc.connectionState)) {
          setParticipants((p) => {
            const next = { ...p };
            delete next[peerId];
            return next;
          });
        }
      };
      if (initiator) {
        (async () => {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          send({ type: "signal", to_id: peerId, signal: { sdp: pc.localDescription } });
        })();
      }
      return pc;
    },
    [send]
  );

  const renegotiate = useCallback(async () => {
    await Promise.all(
      Object.entries(peersRef.current).map(async ([peerId, pc]) => {
        if (pc.signalingState !== "stable") return;
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        send({ type: "signal", to_id: peerId, signal: { sdp: pc.localDescription } });
      })
    );
  }, [send]);

  const pushCaption = (c) =>
    setCaptions((list) => [...list.slice(-5), { ...c, id: `${c.from_id}-${Date.now()}` }]);

  const startCaptionLoop = useCallback(() => {
    if (captionTimerRef.current) return;
    const cycle = async () => {
      const stream = localStreamRef.current;
      if (!stream || !inCallRef.current) return;
      const audio = new MediaStream(stream.getAudioTracks());
      let rec;
      try {
        rec = new MediaRecorder(audio);
      } catch {
        return;
      }
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = async () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        if (blob.size < 3000) return;
        try {
          const fd = new FormData();
          fd.append("audio", blob, "chunk.webm");
          if (callIdRef.current) fd.append("call_id", callIdRef.current);
          const { data } = await api.post(`/rooms/${roomId}/caption`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (data.text) {
            pushCaption({
              from_id: "me",
              from_name: "You",
              text: data.captions?.[myLang] || data.text,
            });
          }
        } catch (err) {
          console.error("Could not handle the call transcript:", err);
        }
      };
      rec.start();
      setTimeout(() => rec.state !== "inactive" && rec.stop(), CHUNK_MS);
    };
    cycle();
    captionTimerRef.current = setInterval(cycle, CHUNK_MS + 400);
  }, [roomId, myLang]);

  const stopCaptionLoop = () => {
    clearInterval(captionTimerRef.current);
    captionTimerRef.current = null;
  };

  const start = async (withVideo) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: withVideo ? { width: 640, height: 480 } : false,
      });
      if (!iceRef.current) iceRef.current = await loadIce();
      localStreamRef.current = stream;
      setVideo(withVideo);
      setCameraOn(withVideo);
      setInCall(true);
      setElapsed(0);
      setMuted(false);
      inCallRef.current = true;
      attachStream("me", "You", stream);
      try {
        const { data } = await api.post(`/rooms/${roomId}/calls`, { video: withVideo });
        callIdRef.current = data.call_id;
      } catch {
        callIdRef.current = null;
      }
      send({ type: "call-join", video: withVideo });
      if (captionsOn) startCaptionLoop();
      return true;
    } catch {
      return false;
    }
  };

  const leave = useCallback(() => {
    stopCaptionLoop();
    Object.values(peersRef.current).forEach((pc) => {
      try {
        pc.close();
      } catch (err) {
        console.debug("call: peer already torn down", err);
      }
    });
    peersRef.current = {};
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    analysersRef.current = {};
    setParticipants({});
    setCaptions([]);
    setSpeaking({});
    setInCall(false);
    setReconnecting(false);
    setElapsed(0);
    inCallRef.current = false;
    send({ type: "call-leave" });
    const finished = callIdRef.current;
    callIdRef.current = null;
    if (finished) api.post(`/rooms/${roomId}/calls/${finished}/end`).catch(() => {});
    return finished;
  }, [send, roomId]);

  const toggleMute = () => {
    const tracks = localStreamRef.current?.getAudioTracks() || [];
    const next = !muted;
    tracks.forEach((t) => (t.enabled = !next));
    setMuted(next);
  };

  // Camera can be switched on mid-call, even if the call started as voice-only.
  const toggleCamera = async () => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const track = stream.getVideoTracks()[0];
    if (track) {
      track.enabled = !track.enabled;
      setCameraOn(track.enabled);
      return;
    }
    try {
      const extra = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 } });
      const vt = extra.getVideoTracks()[0];
      stream.addTrack(vt);
      Object.values(peersRef.current).forEach((pc) => pc.addTrack(vt, stream));
      setVideo(true);
      setCameraOn(true);
      attachStream("me", "You", stream);
      await renegotiate();
    } catch (err) {
      console.error("Could not turn the camera on:", err);
    }
  };

  const toggleCaptions = () => {
    setCaptionsOn((on) => {
      if (on) stopCaptionLoop();
      else if (inCallRef.current) startCaptionLoop();
      return !on;
    });
  };

  // Handle signaling + caption events coming from the shared room socket.
  const handleEvent = useCallback(
    async (evt) => {
      if (evt.type === "caption") {
        if (evt.from_id === undefined) return;
        pushCaption({
          from_id: evt.from_id,
          from_name: evt.from_name || "Guest",
          text: evt.captions?.[myLang] || evt.captions?.[evt.source_language] || "",
        });
        return;
      }
      if (!inCallRef.current) return;
      if (evt.type === "call-join") {
        makePeer(evt.from_id, evt.from_name, true);
      } else if (evt.type === "call-leave") {
        peersRef.current[evt.from_id]?.close();
        delete peersRef.current[evt.from_id];
        setParticipants((p) => {
          const next = { ...p };
          delete next[evt.from_id];
          return next;
        });
      } else if (evt.type === "signal") {
        const pc = makePeer(evt.from_id, evt.from_name, false);
        const sig = evt.signal || {};
        if (sig.sdp) {
          // Both sides can renegotiate (camera switched on) — roll back on a collision.
          if (sig.sdp.type === "offer" && pc.signalingState !== "stable") {
            try {
              await pc.setLocalDescription({ type: "rollback" });
            } catch (err) {
              console.debug("call: rollback not needed", err);
            }
          }
          await pc.setRemoteDescription(new RTCSessionDescription(sig.sdp));
          if (sig.sdp.type === "offer") {
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            send({ type: "signal", to_id: evt.from_id, signal: { sdp: pc.localDescription } });
          }
        } else if (sig.candidate) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(sig.candidate));
          } catch (err) {
            console.error("Rejected an ICE candidate:", err);
          }
        }
      }
    },
    [makePeer, send, myLang]
  );

  // Call duration.
  useEffect(() => {
    if (!inCall) return;
    const iv = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(iv);
  }, [inCall]);

  // Who is talking right now.
  useEffect(() => {
    if (!inCall) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!audioCtxRef.current || audioCtxRef.current.state === "closed") audioCtxRef.current = new Ctx();
    const ctx = audioCtxRef.current;
    const iv = setInterval(() => {
      const next = {};
      Object.entries(participants).forEach(([id, p]) => {
        if (!p.stream || !p.stream.getAudioTracks().length) return;
        let a = analysersRef.current[id];
        if (!a) {
          try {
            const src = ctx.createMediaStreamSource(p.stream);
            const node = ctx.createAnalyser();
            node.fftSize = 512;
            src.connect(node);
            a = { node, data: new Uint8Array(node.frequencyBinCount) };
            analysersRef.current[id] = a;
          } catch (err) {
            return;
          }
        }
        a.node.getByteFrequencyData(a.data);
        const avg = a.data.reduce((s, v) => s + v, 0) / a.data.length;
        next[id] = avg > 18;
      });
      setSpeaking(next);
    }, 300);
    return () => clearInterval(iv);
  }, [inCall, participants]);

  // Rough connection quality from the peer stats.
  useEffect(() => {
    if (!inCall) return;
    const iv = setInterval(async () => {
      let worst = 0;
      await Promise.all(
        Object.values(peersRef.current).map(async (pc) => {
          try {
            const stats = await pc.getStats();
            stats.forEach((r) => {
              if (r.type === "candidate-pair" && r.state === "succeeded" && r.currentRoundTripTime)
                worst = Math.max(worst, r.currentRoundTripTime);
            });
          } catch (err) {
            console.debug("call: stats unavailable", err);
          }
        })
      );
      setQuality(worst === 0 ? "good" : worst < 0.2 ? "good" : worst < 0.45 ? "ok" : "poor");
    }, 3000);
    return () => clearInterval(iv);
  }, [inCall]);

  useEffect(() => () => stopCaptionLoop(), []);

  return {
    inCall, video, cameraOn, muted, participants, captions, captionsOn, callIdRef,
    elapsed, speaking, quality, reconnecting,
    start, leave, toggleMute, toggleCamera, toggleCaptions, handleEvent,
  };
}
